import { ButtonTranslucent, Icon } from '@amelio/ui-library';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReactNode } from 'react';

/** Sub-pixel scroll positions never quite reach the end: a pixel of slack still counts as the edge. */
const EDGE_TOLERANCE_PX = 1;

interface PillarCarouselProps<T> {
    items: T[];
    getKey: (item: T) => string;
    renderItem: (item: T) => ReactNode;
    /** Names the scrolling list for assistive technology. */
    ariaLabel: string;
    /**
     * Rendered at the left end of the arrows' row -- a section's title block, for instance. Left
     * out, the row holds the arrows alone, still pushed to the right edge.
     */
    header?: ReactNode;
    dataTestId?: string;
}

interface ScrollEdges {
    canScrollPrevious: boolean;
    canScrollNext: boolean;
}

type Arrow = 'previous' | 'next';

/** One of the two arrows, in their visual order; the library's button takes no ref of its own. */
const findArrow = (arrows: HTMLElement | null, arrow: Arrow) =>
    arrows?.querySelectorAll('button')[arrow === 'previous' ? 0 : 1] ?? null;

/**
 * A horizontal row of tiles that scrolls, snapping tile by tile, with a previous and a next arrow
 * shown only when the row overflows. Each arrow scrolls by one tile and its gutter, and is disabled
 * at its end; an arrow that loses its end while focused hands the focus to the other one, so the
 * keyboard never falls back to the top of the page. The tiles stay in the tab order: focusing one
 * scrolls it into view. How many tiles fit a view is `.obj-pillar-tile`'s concern (`app.css`).
 */
export default function PillarCarousel<T>({
    items,
    getKey,
    renderItem,
    ariaLabel,
    header,
    dataTestId,
}: PillarCarouselProps<T>) {
    const { t } = useTranslation();
    const trackRef = useRef<HTMLUListElement>(null);
    const arrowsRef = useRef<HTMLDivElement>(null);
    const arrowToFocusRef = useRef<Arrow | null>(null);
    const [edges, setEdges] = useState<ScrollEdges>({
        canScrollPrevious: false,
        canScrollNext: false,
    });

    const updateEdges = useCallback(() => {
        const track = trackRef.current;
        if (!track) return;

        const maxScrollLeft = track.scrollWidth - track.clientWidth;
        const nextEdges = {
            canScrollPrevious: track.scrollLeft > EDGE_TOLERANCE_PX,
            canScrollNext: track.scrollLeft < maxScrollLeft - EDGE_TOLERANCE_PX,
        };

        // Read from the track's own root: on the host the module renders inside a shadow root,
        // whose focused element `document.activeElement` does not see. Both sides of the
        // comparison can be absent -- no arrow is drawn until the first measure lands, and a
        // shadow root reports no focused element at all -- so an arrow that is not there is
        // never the focused one.
        const focused = (track.getRootNode() as Document | ShadowRoot).activeElement;
        const holdsFocus = (arrow: Arrow) => {
            const element = findArrow(arrowsRef.current, arrow);

            return element !== null && element === focused;
        };

        if (!nextEdges.canScrollNext && holdsFocus('next')) {
            arrowToFocusRef.current = 'previous';
        } else if (!nextEdges.canScrollPrevious && holdsFocus('previous')) {
            arrowToFocusRef.current = 'next';
        }

        setEdges(nextEdges);
    }, []);

    useEffect(() => {
        const arrow = arrowToFocusRef.current;
        if (!arrow) return;

        arrowToFocusRef.current = null;
        findArrow(arrowsRef.current, arrow)?.focus();
    }, [edges]);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;

        updateEdges();
        const observer = new ResizeObserver(updateEdges);
        observer.observe(track);

        return () => observer.disconnect();
    }, [items.length, updateEdges]);

    const scrollByOneTile = (direction: -1 | 1) => {
        const track = trackRef.current;
        const firstTile = track?.firstElementChild;
        if (!track || !firstTile) return;

        const gutter = parseFloat(getComputedStyle(track).columnGap) || 0;
        track.scrollBy({
            left: direction * (firstTile.getBoundingClientRect().width + gutter),
            behavior: 'smooth',
        });
    };

    const hasOverflow = edges.canScrollPrevious || edges.canScrollNext;

    return (
        <div
            className={'flex flex-col gap-4'}
            data-testid={dataTestId}
        >
            {(header || hasOverflow) && (
                <div className={'flex flex-wrap items-center gap-4'}>
                    {header}

                    {hasOverflow && (
                        <div
                            ref={arrowsRef}
                            className={'ml-auto flex gap-2'}
                        >
                            <ButtonTranslucent
                                size={'md'}
                                onClick={() => scrollByOneTile(-1)}
                                disabled={!edges.canScrollPrevious}
                                ariaLabel={t('objectives.pillars.scrollPrevious')}
                                leftIcon={
                                    <Icon
                                        name={'arrow-arrow-left'}
                                        size={'md'}
                                        color={'inherit'}
                                    />
                                }
                                dataTestId={'pillar-carousel-previous'}
                            />

                            <ButtonTranslucent
                                size={'md'}
                                onClick={() => scrollByOneTile(1)}
                                disabled={!edges.canScrollNext}
                                ariaLabel={t('objectives.pillars.scrollNext')}
                                leftIcon={
                                    <Icon
                                        name={'arrow-arrow-right'}
                                        size={'md'}
                                        color={'inherit'}
                                    />
                                }
                                dataTestId={'pillar-carousel-next'}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* A scrolling box clips on both axes: the padding, cancelled by the same negative
                margin, leaves room for a tile's hover zoom without shifting the row off the page
                edges, and the scroll padding snaps each tile to that same inner edge. */}
            <ul
                ref={trackRef}
                onScroll={updateEdges}
                aria-label={ariaLabel}
                className={
                    'obj-pillar-grid scrollbar-hide -m-3 flex snap-x snap-mandatory scroll-px-3 overflow-x-auto p-3'
                }
                data-testid={'pillar-carousel-track'}
            >
                {items.map((item) => (
                    <li
                        key={getKey(item)}
                        className={'obj-pillar-tile flex-shrink-0 snap-start'}
                    >
                        {renderItem(item)}
                    </li>
                ))}
            </ul>
        </div>
    );
}
